import { Icon } from "@iconify/react";
import {
  Button,
  Col,
  Form,
  Input,
  Row,
  Space,
  Typography,
  Modal,
  Select, // Import Select component
} from "antd";
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import { ContentWrapper } from "~/Components";
import styles from "../../Contract.module.scss";
import { ErrorMessage, Field, FieldProps, Formik } from "formik";
import { useAuth } from "~/Hooks/useAuth";
import { useNotification } from "~/Hooks/useNotification";
import { get, post, put } from "@services/api.service";
import { SuccessModal } from "@components/SuccessModal/SuccessModal";
import { useQuery } from "~/Hooks/useQuery";
import { sortBy, uniqueId } from "lodash";

interface FormItems {
  oneStopUsers: {
    userName: string;
    userEmail: string;
    designation: string;
  }[];

  recipientLock: boolean;
}

const defaultValue = {
  oneStopUsers: [
    {
      userName: "",
      userEmail: "",
      designation: "One Stop Centre, PCD",
    },
  ],
  recipientLock: false,
} as FormItems;

export const AddNewOneStopCentre = () => {
  const { user } = useAuth();
  const { openToast, openNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [initialValue, setInitialValue] = useState<FormItems>(defaultValue);
  const [openModal, setOpenModal] = useState(false);
  const [oneStopCentre, setOneCentreValues] = useState<FormItems>();
  const [successOpen, setSuccessOpen] = useState(false);
  const { id } = useParams();
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [selectedNames, setSelectedNames] = useState<string[]>([]);

  const navigate = useNavigate();
  const query = useQuery();
  const uniqueId = query?.get("uniqueId") || "";
  const viewItem = query?.get("view") || "";
  useEffect(() => {
    if (uniqueId) {
      get(`v1/spa/one-stop-center/get-detail?uniqueId=${uniqueId}`)
        .then((res: any) => {
          const values = {
            oneStopUsers: [
              {
                userName: res.response.data.userName,
                userEmail: res.response.data.userEmail,
              },
            ],
          } as unknown as FormItems;
          setInitialValue(values);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, []);

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      get(`v1/spa/subsidiary/update-subsidiary?templateId=${id}`)
        .then((res: any) => {
          res = res.response.data;
          const values = {
            oneStopUsers: [
              {
                userName: res.data.userName,
                userEmail: res.data.userEmail,
                designation: "One Stop Centre, PCD",
              },
            ],
          } as FormItems;
          setInitialValue(values);
          setIsLoading(false);
        })
        .catch(() => {
          openToast({
            content: "Getting data failed",
            type: "error",
          });
          setIsLoading(false);
        });
    }
  }, [id, openToast]);

  const onSubmit = (values: FormItems) => {
    // setIsSubmitLoading(true);
    setOpenModal(true);
    setOneCentreValues(values);
  };

  const [approverData, setApproverData] = useState([]);
  const [approverDataByEmail, setApproverDataByEmail] = useState<any>([]);

  const getUserRecords = () => {
    const subsidiaryUrl = `v1/user/get-active-user-list`;
    get(subsidiaryUrl)
      .then((res: any) => {
        if (res && res.response && res.response.data) {
          const responseData = res.response.data;
          if (responseData) {
            setApproverData(responseData);
            setApproverDataByEmail(sortBy(responseData, ["email"]));
          } else {
            console.error("No records found in the response");
            setApproverData([]);
          }
        } else {
          console.error("Unexpected response structure:", res);
        }
      })
      .catch((err) => {
        console.error("API Error:", err);
        openToast({
          content: "Getting Approvers failed",
          type: "error",
        });
      });
  };

  useEffect(() => {
    getUserRecords();
  }, []);

  const onHandleSubmit = async () => {
    setIsSubmitLoading(true);
    if (!oneStopCentre) {
      return;
    }

    // let approver: any = [...values.approver];

    const { recipientLock, ...payload } = oneStopCentre;
    let updatePayload;

    updatePayload = payload.oneStopUsers.map((item: any) => ({
      ...item,
      uniqueId: uniqueId,
    }));

    setIsSubmitLoading(true);
    if (uniqueId) {
      put(`v1/spa/one-stop-center/update-one-stop-center`, updatePayload[0])
        .then((res: any) => {
          if (res) {
            setSuccessOpen(true);
          }
          setIsSubmitLoading(false);
        })
        .catch((err) => {
          setOpenModal(false);
          openNotification({
            title: err,
            message: err.message || "Secretariat, PCD Updating failed",
            type: "error",
          });
          setIsSubmitLoading(false);
        });
    } else {
      post(`v1/spa/one-stop-center/create-one-stop-center`, payload.oneStopUsers)
        .then((res: any) => {
          setSuccessOpen(true);
          setIsSubmitLoading(false);
        })
        .catch((err) => {
          setOpenModal(false);
          // openNotification({
          //   title: err,
          //   message: "One Stop Centre Creation failed",
          //   type: "error",
          // });
          setIsSubmitLoading(false);
        });
    }
  };

  const onCancel = () => {
    setOpenModal(false);
    setIsSubmitLoading(false);
  };

  const validationSchema1 = yup.object().shape({
    oneStopUsers: yup.array().of(
      yup.object().shape({
        userName: yup
          .string()
          .trim()
          .required("User Name is required")
          .matches(/^\S(?:.*\S)?$/, "User Name cannot start or end with whitespace"),
        userEmail: yup.string().trim().email("Invalid email address").required("User Email is required"),
      })
    ),
  });

  return (
    <>
      <ContentWrapper
        loading={isLoading}
        customTitle={
          <Space align="start">
            <Icon
              onClick={() => navigate(-1)}
              icon="akar-icons:arrow-left"
              style={{
                fontSize: "1.8rem",
                cursor: "pointer",
                color: styles.primary,
                marginRight: styles.whitespace1,
              }}
            />
            <span>
              <Typography.Title
                level={3}
                style={{ margin: 0 }}
              >
                {viewItem ? "View" : uniqueId ? "Edit" : "Create"} Secretariat, PCD
              </Typography.Title>
            </span>
          </Space>
        }
      >
        <Formik
          initialValues={initialValue}
          onSubmit={onSubmit}
          validationSchema={validationSchema1}
        >
          {(props) => {
            const { setFieldValue, setFieldTouched } = props;

            useEffect(() => {
              initialValue.oneStopUsers.map((subsidiary: any, index: any) => {
                if (subsidiary) {
                  setFieldValue(`oneStopUsers[${index}].userEmail`, subsidiary.userEmail);
                  setFieldValue(`oneStopUsers[${index}].userName`, subsidiary.userName);
                }
              });
            }, [initialValue]);

            useEffect(() => {
              let index = initialValue.oneStopUsers.length - 1;
              setTimeout(() => {
                setFieldTouched(`oneStopUsers[${index}].email`, false);
                setFieldTouched(`oneStopUsers[${index}].name`, false);
              }, 0);
            }, [uniqueId, initialValue, props.values]);
            const adminsMail = props.values.oneStopUsers.map((val: any) => {
              return val.userEmail;
            });

            const adminsName = props.values.oneStopUsers.map((val: any) => {
              return val.userName;
            });

            return (
              <>
                <Form
                  layout="vertical"
                  onFinish={props.handleSubmit}
                >
                  <Row style={{ marginTop: 16 }}>
                    <Col
                      xs={24}
                      md={16}
                    >
                      <div id="step1">
                        <div>
                          <Row>
                            <Col span={12}>
                              <div style={{ textAlign: "left" }}>
                                <Typography.Title
                                  level={4}
                                  style={{ marginBottom: styles.whitespace3 }}
                                >
                                  User Details
                                </Typography.Title>
                              </div>
                            </Col>
                          </Row>
                          {props.values.oneStopUsers.map((_, i) => (
                            <Row
                              gutter={[16, 16]}
                              align="top"
                              key={`user${i}`}
                            >
                              <Col span={8}>
                                <Form.Item label="Name">
                                  <Field name={`oneStopUsers[${i}].userName`}>
                                    {({ field, form }: FieldProps) => (
                                      <Select
                                        getPopupContainer={(triggerNode) => triggerNode.parentNode}
                                        className={viewItem ? "viewItem" : "none"}
                                        disabled={viewItem ? true : false}
                                        showSearch
                                        {...field}
                                        placeholder="Select name"
                                        onChange={(value, option: any) => {
                                          const selectedEmail = option ? option.email : "";
                                          form.setFieldValue(`oneStopUsers[${i}].userEmail`, selectedEmail);
                                          form.setFieldValue(`oneStopUsers[${i}].userName`, value);

                                          const updatedSelectedNames = value
                                            ? [...selectedNames, value]
                                            : selectedNames.filter((name) => name !== field.value);
                                          setSelectedNames(updatedSelectedNames);
                                        }}
                                      >
                                        {approverData
                                          .filter((user: any) => !adminsName.includes(user.name))
                                          .map((user: any, index: any) => (
                                            <Select.Option
                                              key={index}
                                              value={user.name}
                                              email={user.email}
                                            >
                                              {user.name}
                                            </Select.Option>
                                          ))}
                                      </Select>
                                    )}
                                  </Field>
                                  <Typography.Text type="danger">
                                    <ErrorMessage name={`oneStopUsers[${i}].userName`} />
                                  </Typography.Text>
                                </Form.Item>
                              </Col>
                              <Col span={8}>
                                <Form.Item label="Email">
                                  <Field name={`oneStopUsers[${i}].userEmail`}>
                                    {({ field, form }: FieldProps) => (
                                      <Select
                                        getPopupContainer={(triggerNode) => triggerNode.parentNode}
                                        className={viewItem ? "viewItem" : "none"}
                                        disabled={viewItem ? true : false}
                                        showSearch
                                        {...field}
                                        style={{ width: "100%" }}
                                        placeholder="Select an email"
                                        onChange={(value, option: any) => {
                                          const selectedName = option ? option.label : "";
                                          form.setFieldValue(`oneStopUsers[${i}].userEmail`, value);
                                          form.setFieldValue(`oneStopUsers[${i}].userName`, selectedName);

                                          const updatedSelectedEmails = value
                                            ? [...selectedEmails, value]
                                            : selectedEmails.filter((email) => email !== field.value);
                                          setSelectedEmails(updatedSelectedEmails);
                                        }}
                                        filterOption={(input, option: any) =>
                                          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                        }
                                      >
                                        {approverDataByEmail
                                          .filter((email: any) => !adminsMail.includes(email.email))
                                          .map((email: any, index: any) => (
                                            <Select.Option
                                              key={index}
                                              value={email.email}
                                              label={email.name}
                                            >
                                              {email.email}
                                            </Select.Option>
                                          ))}
                                      </Select>
                                    )}
                                  </Field>
                                  <Typography.Text type="danger">
                                    <ErrorMessage name={`oneStopUsers[${i}].userEmail`} />
                                  </Typography.Text>
                                </Form.Item>
                              </Col>
                              {!viewItem && (
                                <Col span={2}>
                                  <Form.Item label=" ">
                                    <Button
                                      type="link"
                                      danger
                                      disabled={props.values.oneStopUsers.length === 1}
                                      onClick={() => {
                                        const oneStopUsers = [...props.values.oneStopUsers];
                                        oneStopUsers.splice(i, 1);
                                        props.setValues({
                                          ...props.values,
                                          oneStopUsers: oneStopUsers ? oneStopUsers : [],
                                        });
                                      }}
                                    >
                                      {" "}
                                      <Icon
                                        icon="fluent:delete-16-regular"
                                        style={{
                                          color: styles.strawberry,
                                          cursor: "pointer",
                                          fontSize: 20,
                                        }}
                                      />
                                    </Button>
                                  </Form.Item>
                                </Col>
                              )}
                            </Row>
                          ))}
                          {!uniqueId && (
                            <Row gutter={16}>
                              <Col
                                span={24}
                                style={{ textAlign: "right", marginBottom: 16 }}
                              >
                                <Button
                                  type="text"
                                  icon={<Icon icon="akar-icons:plus" />}
                                  style={{
                                    cursor: props.values.recipientLock ? "not-allowed" : "auto",
                                  }}
                                  disabled={
                                    props.values.recipientLock ||
                                    props.values.oneStopUsers.some((val) => val.userName === "" || val.userEmail === "")
                                  }
                                  onClick={() =>
                                    props.setValues({
                                      ...props.values,
                                      oneStopUsers: [
                                        ...props.values.oneStopUsers,
                                        {
                                          userName: "",
                                          userEmail: "",
                                          designation: "One Stop Centre, PCD",
                                        },
                                      ],
                                    })
                                  }
                                >
                                  Add Another
                                </Button>
                              </Col>
                            </Row>
                          )}
                        </div>
                      </div>
                      {!viewItem && (
                        <Row>
                          <Col
                            xs={24}
                            md={24}
                            style={{ textAlign: "right" }}
                          >
                            <Button
                              htmlType="submit"
                              type="primary"
                              loading={isSubmitLoading}
                              disabled={!props.isValid}
                            >
                              Submit
                            </Button>
                          </Col>
                        </Row>
                      )}
                    </Col>
                  </Row>
                </Form>
              </>
            );
          }}
        </Formik>
        <SuccessModal
          message={""}
          title={`You have successfully ${
            uniqueId ? "edited the user into Secretariat, PCD" : "added the user into Secretariat, PCD"
          } `}
          type={"success"}
          buttonText={"Go Back"}
          redirect={`/one-stop-centre`}
          open={successOpen}
          setOpen={setSuccessOpen}
        />
      </ContentWrapper>
      <Modal
        open={openModal}
        onCancel={onCancel}
        closable={false}
        width={400}
        style={{ maxHeight: 300 }}
        centered
        footer={[
          <div style={{ textAlign: "center", marginTop: "20px" }}>
            <Button
              onClick={onCancel}
              style={{ marginRight: "10px" }}
            >
              Cancel
            </Button>
            <Button
              loading={isSubmitLoading}
              type="primary"
              onClick={onHandleSubmit}
            >
              Submit
            </Button>
          </div>,
        ]}
      >
        <h4>{`Are you sure want to ${uniqueId ? "edit" : "add"} this approver to the Secretariat, PCD?`}</h4>
      </Modal>
    </>
  );
};
